import {
  updateEntriesPosition,
  filterDeletedEntry,
} from 'apps/builder/store/reducers/entities/entries'
import { reIndexOnDelete } from 'apps/formsCreationKit/lib/utils'
import {
  deleteElement,
  deleteTableColumn,
  addTableColumn,
} from 'apps/studio/store/actions/sharedActions'
import { copyEntry } from 'apps/formsCreationKit/store/actions/sharedActions'
import { createGuid } from 'apps/builder/lib/utils'
import { lib } from '@gocanvas/client'
const { DEFAULT_SHEET_TYPE_ID, LABELS_ABOVE_STYLE, LOOP_SHEET_TYPE_ID } = lib

export const guidNumber = () => `temp-${Math.floor(Math.random() * 1e12)}`

export const createDefaultPageData = (pageGuid, pageNumber) => ({
  id: pageGuid,
  section_ids: [],
  page_number: pageNumber,
  styles: {},
})

export const createDefaultLayoutData = pageGuid => ({
  id: guidNumber(),
  parent_layout_id: null,
  section_id: null,
  page_id: pageGuid,
  layout_type: 'absolute',
  sort_order: 0,
  position: {},
  styles: {},
})

export const resolveTableSheets = (sheets, sectionId) => {
  const keyFieldSheet = sheets.find(s => s.section_id === sectionId)
  const tableSheet = sheets.find(
    s => s.section_id === keyFieldSheet?.multi_section
  )
  return { keyFieldSheet, tableSheet }
}

export const entriesForTableSheets = (entries, keyFieldSheet, tableSheet) =>
  entries.filter(
    e => e.sheet_id === keyFieldSheet?.id || e.sheet_id === tableSheet?.id
  )

export const createScreenData = pageNumber => {
  const sectionGuid = guidNumber()
  const sheetGuid = guidNumber()

  const section = {
    id: sectionGuid,
    description: `Page ${pageNumber}`,
    position: pageNumber - 1,
  }

  const sheet = {
    id: sheetGuid,
    section_id: sectionGuid,
    sheet_type_id: DEFAULT_SHEET_TYPE_ID,
    style: LABELS_ABOVE_STYLE,
    description: `Page ${pageNumber}`,
    position: 0,
    pdf_visibility: 0,
    // TODO: Why were we setting page_number on the sheet before? Do we need it?
    page_number: pageNumber,
  }

  return { section, sheet }
}

export const STATIC_TABLE_COLS = 4
export const STATIC_TABLE_DATA_ROWS = 3
export const STATIC_TABLE_HEADER_ROWS = 1
export const STATIC_TABLE_WIDTH_RATIO = 0.75
export const STATIC_TABLE_ROW_HEIGHT = 40
export const STATIC_TABLE_TITLE_HEIGHT = 32
export const STATIC_TABLE_DEFAULT_TITLE = 'Title'

// Get a cell's { x, y, width, height } from table's columns/rows.
// for selectors / dnd / toolbar).
export const getTableCellPosition = (
  tableLayout,
  sortOrder,
  { origin = 'frame' } = {}
) => {
  const { columns = [], rows = [], position: tablePos = {} } = tableLayout
  const colCount = columns.length || 1
  const idx = (sortOrder ?? 1) - 1
  const rowIdx = Math.floor(idx / colCount)
  const colIdx = idx % colCount
  const colOffset = columns.slice(0, colIdx).reduce((s, c) => s + c.width, 0)
  const rowOffset = rows
    .slice(0, rowIdx)
    .reduce((s, r) => s + (r.height ?? STATIC_TABLE_ROW_HEIGHT), 0)
  const baseX = origin === 'page' ? tablePos.x ?? 0 : 0
  const baseY = origin === 'page' ? tablePos.y ?? 0 : 0
  return {
    x: baseX + colOffset,
    y: baseY + STATIC_TABLE_TITLE_HEIGHT + rowOffset,
    width: columns[colIdx]?.width ?? 0,
    height: rows[rowIdx]?.height ?? STATIC_TABLE_ROW_HEIGHT,
  }
}

export const createTableData = ({
  pageId,
  pageNumber,
  rootLayout,
  pageWidth,
  pageHeight,
  entryType,
  nextSortOrder = 0,
  position,
  sectionPosition,
}) => {
  const cols = STATIC_TABLE_COLS
  const dataRows = STATIC_TABLE_DATA_ROWS
  const headerRows = STATIC_TABLE_HEADER_ROWS
  const totalRows = headerRows + dataRows

  const tableWidth = pageWidth * STATIC_TABLE_WIDTH_RATIO
  const columnWidth = tableWidth / cols
  const tableHeight =
    STATIC_TABLE_TITLE_HEIGHT + totalRows * STATIC_TABLE_ROW_HEIGHT

  const defaultX = (pageWidth - tableWidth) / 2
  const defaultY = pageHeight ? Math.max(0, (pageHeight - tableHeight) / 2) : 0
  const x = position?.x ?? defaultX
  const y = position?.y ?? defaultY

  const sectionGuid = guidNumber()
  const sheetGuid = guidNumber()
  const multiSheetGuid = guidNumber()
  const multiSectionGuid = guidNumber()

  const layoutId = guidNumber()
  const layout = {
    id: layoutId,
    parent_layout_id: rootLayout?.id,
    section_id: sectionGuid,
    page_id: pageId,
    layout_type: 'table',
    sort_order: nextSortOrder,
    position: { x, y, width: tableWidth, height: tableHeight },
    styles: {},
    columns: Array.from({ length: cols }, () => ({ width: columnWidth })),
    rows: Array.from({ length: totalRows }, () => ({
      height: STATIC_TABLE_ROW_HEIGHT,
    })),
  }

  // Column 1 is key field
  // Columns 2..n are nested entries on the TableScreen (detail) sheet
  const entries = Array.from({ length: cols }, (_, c) => {
    const isKey = c === 0

    return isKey
      ? {
          ...entryType,
          guid: guidNumber(),
          label: `Column ${c + 1}`,
          keyField: true,
        }
      : {
          ...entryType,
          guid: guidNumber(),
          sheet_id: multiSheetGuid,
          position: c - 1,
          label: `Column ${c + 1}`,
          keyField: false,
        }
  })

  const elements = entries.map((entry, c) => ({
    page_number: pageNumber,
    page_id: pageId,
    layout_id: layoutId,
    entry_guid: entry.guid,
    element_type: entry.type,
    sort_order: c + 1,
    position: {},
  }))

  return {
    id: sheetGuid,
    section_id: sectionGuid,
    sheet_type_id: LOOP_SHEET_TYPE_ID,
    style: 1,
    autoFocus: false,
    placeholder: 'Table',
    position: 0,
    keyField: entries[0],
    multiSheetGuid,
    multiSectionGuid,
    sectionPosition,
    layout,
    defaultEntries: entries.filter(e => !e.keyField),
    elements,
  }
}

// ---------------------------------------------------------------------------
// Column / row payload builders
// ---------------------------------------------------------------------------

const MIN_COL_WIDTH = 20

export const buildTextEntryType = entryTypes => {
  const t = entryTypes.find(({ type }) => type === 'Text')
  return t ? { ...t, entry_type_id: t.id } : null
}

// Scales columns to fit within maxWidth pdf page
const fitColumnsToWidth = (columns, maxWidth) => {
  const total = columns.reduce((s, c) => s + c.width, 0)
  if (total <= maxWidth) return columns
  const scale = maxWidth / total
  return columns.map(c => ({
    ...c,
    width: Math.max(Math.round(c.width * scale), MIN_COL_WIDTH),
  }))
}

// sort_order for a header element: colIdx + 1, where colIdx = keyField ? 0 : position + 1
const headerElementSortOrder = entry =>
  (entry.keyField ? 0 : entry.position + 1) + 1

// Rebuild every header element's sort_order from the finalized entry map.
const renumberHeaderElements = (elements, entriesByGuid) =>
  elements.map(el => {
    const entry = entriesByGuid.get(el.entry_guid)
    return entry ? { ...el, sort_order: headerElementSortOrder(entry) } : el
  })

export const buildAddColumnPayload = ({
  tableLayout,
  tableSheet,
  entries,
  headerElements,
  insertAt,
  pageWidth,
  entry,
  entryType,
}) => {
  const { id: layoutId, columns = [], position: pos = {} } = tableLayout
  const colCount = columns.length

  // Insert index k, clamped to [1, colCount] — the key field at index 0 is
  // never displaced, and inserting at colCount appends on the right.
  const k = Math.max(1, Math.min(insertAt, colCount))

  // New column width: average of existing, at least MIN_COL_WIDTH
  const avgWidth = Math.round(
    columns.reduce((s, c) => s + c.width, 0) / colCount
  )
  const newColWidth = Math.max(avgWidth, MIN_COL_WIDTH)

  // Insert the new column, then clamp to the space available on the page.
  // The table's left edge stays put; it grows to the right.
  const posX = pos.x ?? 0
  let newColumns = [
    ...columns.slice(0, k),
    { width: newColWidth },
    ...columns.slice(k),
  ]
  // Only clamp when we know the page bounds (pageWidth > 0).
  if (pageWidth) newColumns = fitColumnsToWidth(newColumns, pageWidth - posX)
  const finalWidth = newColumns.reduce((s, c) => s + c.width, 0)

  const newPosition = { ...pos, x: posX, width: finalWidth }

  // New entry on the detail tableSheet (never the keyFieldSheet)
  // If entry is passed in, we are duplicating an entry, otherwise we are adding a new entry so we use base entryType
  const newEntryData = entry || entryType
  const newEntry = {
    ...newEntryData,
    guid: guidNumber(),
    sheet_id: tableSheet.id,
    position: k - 1, // tableSheet position is colIdx - 1
    label: entry?.label || `Column ${colCount + 1}`,
    keyField: false,
  }

  // Compute finalized positions for all table entries after the position shift,
  // so we can derive the correct sort_order for each header element.
  const tableSheetEntriesCount = entries.filter(
    e => e.sheet_id === tableSheet.id
  ).length
  const finalizedEntries = [...entries, newEntry].map(
    updateEntriesPosition(newEntry, tableSheetEntriesCount)
  )
  const entriesByGuid = new Map(finalizedEntries.map(e => [e.guid, e]))

  // New header element (rowIdx 0); sort_order is corrected by renumber below
  const refEl = headerElements[0] ?? {}
  const newElement = {
    page_number: refEl.page_number,
    page_id: refEl.page_id,
    layout_id: layoutId,
    entry_guid: newEntry.guid,
    element_type: newEntry.type,
    sort_order: 0,
    position: {},
  }

  const headerElementsFinal = renumberHeaderElements(
    [...headerElements, newElement],
    entriesByGuid
  )

  return {
    layout: { id: layoutId, position: newPosition, columns: newColumns },
    entries: finalizedEntries,
    sheetIds: [...new Set(finalizedEntries.map(e => e.sheet_id))],
    headerElements: headerElementsFinal,
    newEntryGuid: newEntry.guid,
    tableSheetId: tableSheet.id,
  }
}

export const buildDeleteColumnPayload = ({
  tableLayout,
  tableSheet,
  entries,
  headerElements,
  colIdx,
}) => {
  const { id: layoutId, columns = [], position: pos = {} } = tableLayout
  const colCount = columns.length

  // Guard: keep the key field plus at least one data column, and never the
  // key field column (index 0).
  if (colCount <= 2 || colIdx <= 0 || colIdx >= colCount) return null

  // The target data column's entry lives on tableSheet at position = colIdx - 1
  const removedEntry = entries.find(
    e => e.sheet_id === tableSheet.id && e.position === colIdx - 1
  )
  if (!removedEntry) return null

  // Drop the target column — remaining columns keep their widths
  const newColumns = [...columns.slice(0, colIdx), ...columns.slice(colIdx + 1)]
  const finalWidth = newColumns.reduce((s, c) => s + c.width, 0)
  const newPosition = { ...pos, width: finalWidth }

  // Shift the remaining tableSheet entries down to close the gap.
  const remainingEntries = reIndexOnDelete(
    filterDeletedEntry(entries, removedEntry.guid),
    removedEntry.position,
    tableSheet.id
  )
  const entriesByGuid = new Map(remainingEntries.map(e => [e.guid, e]))

  // Remove deleted element and renumber the rest
  const remainingElements = headerElements.filter(
    el => el.entry_guid !== removedEntry.guid
  )
  const headerElementsFinal = renumberHeaderElements(
    remainingElements,
    entriesByGuid
  )

  return {
    layout: { id: layoutId, position: newPosition, columns: newColumns },
    entries: remainingEntries,
    sheetIds: [...new Set(remainingEntries.map(e => e.sheet_id))],
    headerElements: headerElementsFinal,
  }
}

// Data rows carry no entries/elements (only the header row does), so adding or
// removing a row only adjusts the rows array and the table height. The table's
// top edge stays put; it grows/shrinks downward.
export const buildAddRowPayload = ({ tableLayout, insertAt }) => {
  const { id: layoutId, rows = [], position: pos = {} } = tableLayout

  // Insert index k, clamped to [1, rows.length] — the header row (rowIdx 0)
  // is never displaced, and inserting at rows.length appends at the bottom.
  const k = Math.max(1, Math.min(insertAt, rows.length))

  const newRows = [
    ...rows.slice(0, k),
    { height: STATIC_TABLE_ROW_HEIGHT },
    ...rows.slice(k),
  ]

  const finalHeight =
    STATIC_TABLE_TITLE_HEIGHT +
    newRows.reduce((s, r) => s + (r.height ?? STATIC_TABLE_ROW_HEIGHT), 0)

  return {
    layout: {
      id: layoutId,
      position: { ...pos, height: finalHeight },
      rows: newRows,
    },
  }
}

export const buildDeleteRowPayload = ({ tableLayout, rowIdx }) => {
  const { id: layoutId, rows = [], position: pos = {} } = tableLayout
  const rowCount = rows.length

  // keep the header row plus at least one data row, and never the header (0)
  if (rowCount <= 2 || rowIdx <= 0 || rowIdx >= rowCount) return null

  const removedHeight = rows[rowIdx]?.height ?? STATIC_TABLE_ROW_HEIGHT
  const newRows = [...rows.slice(0, rowIdx), ...rows.slice(rowIdx + 1)]
  const finalHeight = (pos.height ?? 0) - removedHeight

  return {
    layout: {
      id: layoutId,
      position: { ...pos, height: finalHeight },
      rows: newRows,
    },
  }
}

export const getDeleteElementAction = ({
  element,
  layout,
  sheets,
  entries,
  elements,
}) => {
  const { page_id, entry_guid, sort_order } = element
  const { id: layoutId, section_id: layoutSectionId, layout_type } = layout
  if (layout_type === 'table') {
    const { keyFieldSheet, tableSheet } = resolveTableSheets(
      sheets,
      layoutSectionId
    )
    const tableEntries = entriesForTableSheets(
      entries,
      keyFieldSheet,
      tableSheet
    )
    const headerElements = elements.filter(el => el.layout_id === layoutId)
    const payload = buildDeleteColumnPayload({
      tableLayout: layout,
      tableSheet,
      entries: tableEntries,
      headerElements,
      colIdx: sort_order - 1,
    })
    if (payload) {
      return deleteTableColumn(payload)
    }
  } else {
    const { id: sheetId } = sheets.find(s => s.section_id === layoutSectionId)
    const isLastElement =
      elements.filter(el => el.page_id === page_id && el.layout_id === layoutId)
        .length === 1

    return deleteElement({
      element: { entry_guid },
      entry: { guid: entry_guid },
      section: {
        id: isLastElement ? layoutSectionId : null,
      },
      sheet: { id: isLastElement ? sheetId : null },
      page: { id: isLastElement ? page_id : null },
      layout: { id: isLastElement ? layoutId : null },
    })
  }
  return { type: 'NOOP' }
}

export const getDuplicateElementAction = ({
  element,
  layout,
  sheets,
  entries,
  elements,
  pdfPageMeta,
  entry,
}) => {
  const {
    entry_guid,
    sort_order,
    position: { width, height },
  } = element
  const { id: layoutId, section_id: layoutSectionId, layout_type } = layout
  const { originalWidth, originalHeight } = pdfPageMeta
  if (layout_type === 'table') {
    const { keyFieldSheet, tableSheet } = resolveTableSheets(
      sheets,
      layoutSectionId
    )
    const tableEntries = entriesForTableSheets(
      entries,
      keyFieldSheet,
      tableSheet
    )
    const headerElements = elements.filter(el => el.layout_id === layoutId)
    const colIdx = sort_order - 1
    const payload = buildAddColumnPayload({
      tableLayout: layout,
      tableSheet,
      entries: tableEntries,
      headerElements,
      insertAt: colIdx + 1,
      pageWidth: originalWidth,
      entry,
    })
    return addTableColumn(payload)
  } else {
    return copyEntry({
      guid: entry_guid,
      seed: createGuid(),
      maxWidth: originalWidth - width,
      maxHeight: originalHeight - height,
    })
  }
}
